/**
 * useIcd10Lookup — canonical ICD-10 lookup with LRU cache, debounced prefix
 * search, and tri-state per-code resolution.
 *
 * Two-function shape (PRD M2 + post-review hardening):
 *
 * - ``lookup(code)`` — **pure cache read**. No side effects, no fetches, no
 *   state updates. Safe to call from render. Returns ``{ status, description }``
 *   where ``status`` is one of ``empty | loading | known | unknown``.
 *
 * - ``prefetch(code)`` — **effect-only**. Enqueues an uncached code for fetch
 *   and dispatches a re-render via ``forceUpdate``. **Must be called from
 *   ``useEffect`` or an event handler, never from render** — calling it from
 *   render would update a parent's state during a child's render and trigger
 *   React's "Cannot update a component while rendering a different component"
 *   warning. Idempotent: already-cached or already-inflight codes are no-ops.
 *
 * Tri-state contract: a saved code that's still being verified must not render
 * the "invalid" badge. Consumers render the invalid affordance only when
 * ``status === 'unknown'``. While ``status === 'loading'``, render the code
 * with no badge (it may still be valid — we just don't know yet).
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';

import { fetchIcd10ByCode, fetchIcd10ByPrefix } from '../api/referenceClient';

const LRU_CAPACITY = 500;
const PREFIX_DEBOUNCE_MS = 150;

/** @returns {string} normalized (uppercased, periods stripped) form. */
function normalize(code) {
  if (code == null) return '';
  return String(code).trim().toUpperCase().replace(/\./g, '');
}

/** Touch an LRU map entry so it's the most-recently-used. */
function touch(map, key) {
  const value = map.get(key);
  map.delete(key);
  map.set(key, value);
}

/** Evict the oldest entry if the map exceeds capacity. */
function evictIfNeeded(map, capacity) {
  while (map.size > capacity) {
    const oldest = map.keys().next().value;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
}

export default function useIcd10Lookup() {
  // Caches live in refs so reads are synchronous; a tick reducer forces a
  // re-render when async resolutions update the cache.
  const cacheRef = useRef({
    known: /** @type {Map<string, string>} */ (new Map()),
    unknown: /** @type {Set<string>} */ (new Set()),
    inflight: /** @type {Set<string>} */ (new Set()),
  });
  // Queue of codes that ``prefetch`` has flagged for fetch — drained by the
  // post-commit effect below.
  const pendingRef = useRef(/** @type {Set<string>} */ (new Set()));
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const mountedRef = useRef(true);
  const debounceRef = useRef(
    /** @type {{ timer: any, resolve: ((v: any[]) => void) | null, query: string }} */
    ({ timer: null, resolve: null, query: '' })
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current.timer) {
        clearTimeout(debounceRef.current.timer);
        debounceRef.current.timer = null;
      }
      // If a debounce window was still pending when the component unmounted,
      // resolve waiters with [] so they don't hang forever.
      if (debounceRef.current.resolve) {
        debounceRef.current.resolve([]);
        debounceRef.current.resolve = null;
      }
    };
  }, []);

  // Post-commit drain: any code ``prefetch`` queued during the just-finished
  // commit gets its fetch kicked off here, after React has committed.
  useEffect(() => {
    const c = cacheRef.current;
    const pending = pendingRef.current;
    if (pending.size === 0) return undefined;
    const toFetch = Array.from(pending);
    pending.clear();

    for (const n of toFetch) {
      if (c.known.has(n) || c.unknown.has(n) || c.inflight.has(n)) continue;
      c.inflight.add(n);
      fetchIcd10ByCode(n)
        .then((data) => {
          if (!mountedRef.current) return;
          c.inflight.delete(n);
          if (data && data.description) {
            c.known.set(n, data.description);
            evictIfNeeded(c.known, LRU_CAPACITY);
          } else {
            c.unknown.add(n);
          }
          forceUpdate();
        })
        .catch(() => {
          if (!mountedRef.current) return;
          c.inflight.delete(n);
          // Transient errors leave the code unresolved — next prefetch call
          // will re-enqueue and retry. Do not mark as unknown.
          forceUpdate();
        });
    }
    return undefined;
  });

  /**
   * Pure cache read. **Safe to call from render.** Does not trigger fetches.
   *
   * @param {string} code
   * @returns {{ status: 'empty' | 'known' | 'unknown' | 'loading', description: string }}
   */
  const lookup = useCallback((code) => {
    const n = normalize(code);
    if (!n) return { status: 'empty', description: '' };
    const c = cacheRef.current;
    if (c.known.has(n)) {
      touch(c.known, n);
      return { status: 'known', description: /** @type {string} */ (c.known.get(n)) };
    }
    if (c.unknown.has(n)) return { status: 'unknown', description: '' };
    return { status: 'loading', description: '' };
  }, []);

  /**
   * Enqueue a code for background fetch and schedule a re-render so the
   * post-commit drain effect picks it up. **Call only from ``useEffect`` or
   * event handlers, never from render** — calling during a child's render
   * would dispatch state on the parent hook's component and trigger React's
   * cross-component-update warning.
   *
   * @param {string} code
   */
  const prefetch = useCallback((code) => {
    const n = normalize(code);
    if (!n) return;
    const c = cacheRef.current;
    if (c.known.has(n) || c.unknown.has(n) || c.inflight.has(n)) return;
    if (pendingRef.current.has(n)) return;
    pendingRef.current.add(n);
    forceUpdate();
  }, []);

  /**
   * Debounced prefix search. Coalesces rapid input changes; the trailing call
   * wins. Earlier callers' promises resolve with ``[]`` (superseded) so each
   * caller sees results consistent with the query it actually asked for.
   *
   * @param {string} q  Prefix
   * @param {number} [limit]
   * @returns {Promise<Array<{code: string, description: string}>>}
   */
  const searchPrefix = useCallback((q, limit = 25) => {
    const d = debounceRef.current;

    // Any earlier caller within the still-pending window is superseded:
    // resolve their promise with [] so they don't observe trailing-query results.
    if (d.resolve) {
      d.resolve([]);
      d.resolve = null;
    }
    if (d.timer) {
      clearTimeout(d.timer);
      d.timer = null;
    }

    d.query = q || '';
    if (!d.query.trim()) {
      return Promise.resolve([]);
    }

    let myResolve;
    const myPromise = new Promise((resolve) => {
      myResolve = resolve;
    });
    d.resolve = myResolve;
    const myQuery = d.query;

    d.timer = setTimeout(async () => {
      d.timer = null;
      // If a newer call replaced us, our resolve was already swapped out.
      if (d.resolve !== myResolve) return;
      d.resolve = null;
      try {
        const results = await fetchIcd10ByPrefix(myQuery, limit);
        if (!mountedRef.current) {
          myResolve([]);
          return;
        }
        // Warm the LRU cache with successful prefix hits.
        const c = cacheRef.current;
        for (const r of results) {
          if (r && r.code && r.description) {
            c.known.set(normalize(r.code), r.description);
          }
        }
        evictIfNeeded(c.known, LRU_CAPACITY);
        myResolve(results);
      } catch (_err) {
        myResolve([]);
      }
    }, PREFIX_DEBOUNCE_MS);

    return myPromise;
  }, []);

  return { lookup, prefetch, searchPrefix };
}
